<script setup>
import { computed, onMounted, ref } from 'vue'
import { api, asList } from '../api/client'
import { normalizeUser } from '../api/mappers'
import { canCreateOperators, serializeOperator, userRoleLabel, validateOperator } from '../api/users'
import AppIcon from '../components/AppIcon.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import UserDialog from '../components/UserDialog.vue'
import { useSession } from '../composables/useSession'
import { useToast } from '../composables/useToast'

const session = useSession()
const { show } = useToast()
const users = ref([])
const loading = ref(true)
const loadError = ref('')
const dialogOpen = ref(false)
const saving = ref(false)
const createError = ref('')
const search = ref('')
const canCreate = computed(() => canCreateOperators(session.user))

const visibleUsers = computed(() => {
  const term = search.value.trim().toLocaleLowerCase('ru')
  return users.value
    .filter((user) => !term || [user.username, user.fullName, userRoleLabel(user.role)]
      .some((value) => String(value || '').toLocaleLowerCase('ru').includes(term)))
    .sort((a, b) => a.username.localeCompare(b.username, 'ru'))
})

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    users.value = asList(await api.users.list(), ['users']).map(normalizeUser)
  } catch (error) {
    loadError.value = error.message || 'Не удалось загрузить пользователей.'
  } finally {
    loading.value = false
  }
}

function openCreate() {
  if (!canCreate.value) return
  createError.value = ''
  dialogOpen.value = true
}

async function createOperator(form) {
  if (saving.value || !canCreate.value) return
  createError.value = validateOperator(form)
  if (createError.value) return
  saving.value = true
  try {
    const created = normalizeUser(await api.users.createOperator(serializeOperator(form)))
    users.value = [...users.value, created]
    dialogOpen.value = false
    show(`Оператор ${created.username} создан.`)
  } catch (error) {
    createError.value = error.message || 'Не удалось создать оператора.'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page users-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">Команда базы</span>
        <h1>Пользователи</h1>
        <p class="page-subtitle">Администратор управляет CRM. Операторы создают, редактируют и отменяют брони без удаления.</p>
      </div>
      <button v-if="canCreate" type="button" class="button button--primary" @click="openCreate">
        <AppIcon name="plus" :size="18" />
        Создать оператора
      </button>
    </header>

    <section class="filter-bar" aria-label="Поиск пользователей">
      <label class="search-field">
        <AppIcon name="search" :size="18" />
        <span class="sr-only">Поиск пользователей</span>
        <input v-model="search" type="search" placeholder="Имя, логин или роль" />
      </label>
      <button type="button" class="button button--secondary" :disabled="loading" @click="load">
        <AppIcon name="refresh" :size="17" />
        Обновить
      </button>
    </section>

    <LoadingState v-if="loading" />
    <section v-else-if="loadError" class="data-section">
      <p class="form-error" role="alert">{{ loadError }}</p>
      <button type="button" class="button button--secondary" @click="load">Попробовать ещё раз</button>
    </section>
    <section v-else class="data-section" aria-labelledby="users-list-heading">
      <header class="section-header section-header--compact">
        <div>
          <h2 id="users-list-heading">Учётные записи</h2>
          <p>Всего: {{ users.length }}</p>
        </div>
      </header>
      <div v-if="visibleUsers.length" class="table-scroll">
        <table class="data-table users-table">
          <caption class="sr-only">Пользователи CRM</caption>
          <thead>
            <tr>
              <th scope="col">Имя</th>
              <th scope="col">Логин</th>
              <th scope="col">Роль</th>
              <th scope="col">Статус</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in visibleUsers" :key="user.id" :class="{ 'row--muted': !user.isActive }">
              <td data-label="Имя"><strong>{{ user.fullName || 'Не указано' }}</strong></td>
              <td data-label="Логин">{{ user.username }}</td>
              <td data-label="Роль">{{ userRoleLabel(user.role) }}</td>
              <td data-label="Статус">
                <span class="availability" :class="user.isActive ? 'availability--free' : 'availability--inactive'">
                  {{ user.isActive ? 'Активен' : 'Неактивен' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <EmptyState
        v-else
        :title="search ? 'Пользователи не найдены' : 'Пока нет пользователей'"
        :text="search ? 'Попробуйте другой поисковый запрос.' : 'Создайте учётную запись оператора для сотрудника.'"
      />
    </section>

    <UserDialog v-if="canCreate" :open="dialogOpen" :saving="saving" :error="createError" @close="!saving && (dialogOpen = false)" @save="createOperator" />
  </div>
</template>
